import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { abandonedLeadApiService } from "../../../service/admin/abandonedLeadApiService";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import moment from "moment";
import Pagination from "../../../component/library/pagination";
import { getAbandonedLeadDisplayAmounts } from "./abandonedLeadPricing";
import "./style.css";

const VALID_STAGES = new Set(["cart", "payment"]);

const STAGE_COPY = {
  cart: {
    title: "Abandoned Cart",
    subtitle: "Logged-in users who left after adding items to the cart without placing an order.",
  },
  payment: {
    title: "Abandoned Payment",
    subtitle: "Users who reached checkout payment without completing the order.",
  },
};

const STATUS_LABELS = {
  open: { label: "Open", color: "#f59e0b", bg: "#fffbeb" },
  converted: { label: "Order Placed", color: "#10b981", bg: "#f0fdf4" },
  cancelled: { label: "Cancelled", color: "#ef4444", bg: "#fef2f2" },
};

export default function AbandonedLeadsList() {
  const { stage: stageParam } = useParams();
  const stage = String(stageParam || "").toLowerCase();
  const copy = STAGE_COPY[stage] || { title: "Abandoned Leads", subtitle: "" };
  const message = useMessage();

  const [leads, setLeads] = useState([]);
  const [pages, setPages] = useState({ currentPage: 1, totalPages: 1, totalItems: 0 });
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!VALID_STAGES.has(stage)) {
      setLoading(false);
      setLeads([]);
      return;
    }
    fetchLeads();
  }, [page, statusFilter, stage]);

  const fetchLeads = () => {
    if (!VALID_STAGES.has(stage)) return;
    setLoading(true);
    const query = `?page=${page}&limit=20${statusFilter ? `&status=${statusFilter}` : ""}`;
    abandonedLeadApiService
      .getAbandonedLeads(stage, query)
      .then((res) => {
        if (res?.status?.code === 200 && res?.data) {
          setLeads(res.data.leads || []);
          setPages({
            currentPage: res.data.currentPage || 1,
            totalPages: res.data.totalPages || 1,
            totalItems: res.data.total || 0,
          });
        }
      })
      .catch(() => message.showToast("Error fetching leads.", ToastTypes.ERROR))
      .finally(() => setLoading(false));
  };

  if (!VALID_STAGES.has(stage)) {
    return (
      <div className="btl-page">
        <div className="btl-empty">Invalid list. Use Abandoned Cart or Abandoned Payment from the sidebar.</div>
      </div>
    );
  }

  return (
    <div className="btl-page">
      <div className="btl-header">
        <div>
          <h2 className="btl-title">{copy.title}</h2>
          <p className="btl-subtitle">{copy.subtitle}</p>
        </div>
        <div className="btl-stats-badge">
          Total: <strong>{pages.totalItems}</strong>
        </div>
      </div>

      <div className="btl-filters">
        <select
          className="btl-filter-select"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Open (default)</option>
          <option value="converted">Order Placed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {loading ? (
        <div className="btl-loading">Loading leads…</div>
      ) : leads.length === 0 ? (
        <div className="btl-empty">No leads found.</div>
      ) : (
        <>
          <div className="btl-table-wrapper">
            <table className="btl-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th>{stage === "cart" ? "Product total (excl. shipping)" : "Cart value (incl. shipping)"}</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead, idx) => {
                  const statusInfo = STATUS_LABELS[lead.status] || {
                    label: lead.status,
                    color: "#64748b",
                    bg: "#f1f5f9",
                  };
                  const displayMoney = getAbandonedLeadDisplayAmounts(lead).listColumnValue;
                  return (
                    <tr
                      key={lead._id}
                      className="btl-row-clickable"
                      onClick={() => window.open(`/admin/abandoned-leads/${stage}/${lead._id}`, "_blank")}
                      title="Click to view full lead details"
                    >
                      <td>{(pages.currentPage - 1) * 20 + idx + 1}</td>
                      <td className="btl-name-cell">
                        {lead.name || "—"}
                        <span className="btl-row-hint">View details →</span>
                      </td>
                      <td>{lead.email || "—"}</td>
                      <td>{lead.mobile || "—"}</td>
                      <td>
                        {displayMoney
                          ? `₹${Number(displayMoney).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`
                          : "—"}
                      </td>
                      <td>
                        <span
                          className="btl-status-badge"
                          style={{ color: statusInfo.color, background: statusInfo.bg }}
                        >
                          {statusInfo.label}
                        </span>
                      </td>
                      <td>{lead.createdAt ? moment(lead.createdAt).format("DD MMM YYYY, hh:mm A") : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {pages.totalPages > 1 && (
            <div className="btl-pagination">
              <Pagination pages={pages} page={page} setPage={setPage} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
